import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Pagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  Attachment as AttachmentIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/services/adminApi';
import * as XLSX from 'xlsx';
import ReactMarkdown from 'react-markdown';

/**
 * Admin Log Page
 * 관리자 로그 페이지
 */
const LogPage = () => {
  const [userId, setUserId] = useState('');
  const [experimentKey, setExperimentKey] = useState('');
  // 실제 검색에 사용할 필터 값 (검색 버튼 클릭 시 설정됨)
  const [searchUserId, setSearchUserId] = useState('');
  const [searchExperimentKey, setSearchExperimentKey] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; experimentKey: string | null }>({
    open: false,
    experimentKey: null,
  });
  const [attachmentDialog, setAttachmentDialog] = useState<{ open: boolean; urls: string[] }>({
    open: false,
    urls: [],
  });
  const [responseTextDialog, setResponseTextDialog] = useState<{ open: boolean; text: string }>({
    open: false,
    text: '',
  });
  const queryClient = useQueryClient();

  const { data: logsData, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'logs', { userId: searchUserId, experimentKey: searchExperimentKey, page, limit }],
    queryFn: () => adminApi.searchLogs({ 
      userId: searchUserId || undefined, 
      experimentKey: searchExperimentKey || undefined, 
      page, 
      limit 
    }),
    retry: 1,
  });

  // 다운로드할 로그 개수 조회
  const { data: exportCountData, isLoading: isExportCountLoading } = useQuery({
    queryKey: ['admin', 'logs', 'export-count', { userId: searchUserId, experimentKey: searchExperimentKey }],
    queryFn: () => adminApi.getExportLogsCount({ 
      userId: searchUserId || undefined, 
      experimentKey: searchExperimentKey || undefined 
    }),
    retry: 1,
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => adminApi.deleteLogsByExperimentKey(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'logs'] });
      setDeleteDialog({ open: false, experimentKey: null });
    },
    onError: (error: any) => {
      console.error('Failed to delete logs:', error);
    },
  });

  const handleSearch = () => {
    // 검색 버튼 클릭 시 입력값을 검색 파라미터로 설정
    setSearchUserId(userId);
    setSearchExperimentKey(experimentKey);
    setPage(1);
    // queryKey가 변경되므로 자동으로 refetch됨
  };

  const handleReset = () => {
    // 필터 초기화
    setUserId('');
    setExperimentKey('');
    setSearchUserId('');
    setSearchExperimentKey('');
    setPage(1);
    // queryKey가 변경되므로 자동으로 refetch됨
  };

  const handleExport = async () => {
    try {
      const blob = await adminApi.exportLogs({ 
        userId: searchUserId || undefined, 
        experimentKey: searchExperimentKey || undefined 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = searchExperimentKey || searchUserId || 'all';
      a.href = url;
      a.download = `logs_${filename}_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export error:', error);
      alert('엑셀 다운로드에 실패했습니다.');
    }
  };

  const handleDeleteClick = (key: string) => {
    setDeleteDialog({ open: true, experimentKey: key });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.experimentKey) {
      deleteMutation.mutate(deleteDialog.experimentKey);
    }
  };

  // 백엔드는 ApiResponseDto로 응답하므로 logsData.data가 실제 데이터 객체
  const result = logsData?.data || {};
  const logs = Array.isArray(result.logs) ? result.logs : [];
  const total = typeof result.total === 'number' ? result.total : 0;
  const totalPages = typeof result.totalPages === 'number' ? result.totalPages : 0;
  const currentPage = typeof result.page === 'number' ? result.page : typeof result.page === 'string' ? parseInt(result.page, 10) : page;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        텍스트 생성 로그
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="시험 키"
            value={experimentKey}
            onChange={(e) => setExperimentKey(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="사용자 계정"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            size="small"
            fullWidth
          />
          <IconButton
            color="primary"
            onClick={handleSearch}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              width: 40,
              height: 40,
            }}
          >
            <SearchIcon />
          </IconButton>
          {(searchUserId || searchExperimentKey) && (
            <IconButton
              color="default"
              onClick={handleReset}
              sx={{ 
                bgcolor: 'grey.300',
                color: 'grey.800',
                '&:hover': {
                  bgcolor: 'grey.400',
                },
                width: 40,
                height: 40,
              }}
              title="필터 초기화"
            >
              <ClearIcon />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={isLoading || isExportCountLoading || (exportCountData?.data?.count !== undefined && exportCountData.data.count === 0)}
          >
            엑셀 다운로드{' '}
            {isExportCountLoading ? (
              '...'
            ) : exportCountData?.data?.count !== undefined ? (
              (searchUserId || searchExperimentKey) ? (
                `(${exportCountData.data.count}개)`
              ) : (
                `(전체 ${exportCountData.data.count}개)`
              )
            ) : null}
          </Button>
          {searchExperimentKey && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDeleteClick(searchExperimentKey)}
              disabled={isLoading || total === 0}
            >
              전체 삭제 ({searchExperimentKey})
            </Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>시험 키</TableCell>
              <TableCell>사용자 ID</TableCell>
              <TableCell>입력 프롬프트</TableCell>
              <TableCell>응답 텍스트</TableCell>
              <TableCell>토큰 (입력/출력/전체)</TableCell>
              <TableCell>생성일시</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Alert severity="error" sx={{ mb: 0 }}>
                    로그를 불러오는데 실패했습니다.{' '}
                    {error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  로그가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log: any) => (
                <TableRow key={log.id || log.experimentKey}>
                  <TableCell>{log.experimentKey || '-'}</TableCell>
                  <TableCell>{log.userId || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.inputPrompt ? (log.inputPrompt.length > 50 ? `${log.inputPrompt.substring(0, 50)}...` : log.inputPrompt) : '-'}
                      </Box>
                      {log.attachmentUrls && Array.isArray(log.attachmentUrls) && log.attachmentUrls.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => setAttachmentDialog({ open: true, urls: log.attachmentUrls })}
                          sx={{ 
                            padding: 0.5,
                            minWidth: 'auto',
                            width: '24px',
                            height: '24px',
                          }}
                          title="첨부파일 보기"
                        >
                          <AttachmentIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.responseText ? (log.responseText.length > 50 ? `${log.responseText.substring(0, 50)}...` : log.responseText) : '-'}
                      </Box>
                      {log.responseText && log.responseText.length > 50 && (
                        <IconButton
                          size="small"
                          onClick={() => setResponseTextDialog({ open: true, text: log.responseText })}
                          sx={{ 
                            padding: 0.5,
                            minWidth: 'auto',
                            width: '24px',
                            height: '24px',
                          }}
                          title="상세보기"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {log.inputTokens || 0}/{log.outputTokens || 0}/{log.totalTokens || 0}
                  </TableCell>
                  <TableCell>
                    {log.createdAt ? new Date(log.createdAt).toLocaleString('ko-KR') : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, value) => {
              setPage(value);
              // 페이지 변경 시 스크롤을 테이블 상단으로 이동
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        전체 {total}개 로그 {totalPages > 1 && `(페이지 ${currentPage} / ${totalPages})`}
      </Typography>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, experimentKey: null })}>
        <DialogTitle>로그 삭제 확인</DialogTitle>
        <DialogContent>
          <DialogContentText>
            experimentKey "{deleteDialog.experimentKey}"의 모든 로그를 삭제하시겠습니까?
            이 작업은 되돌릴 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, experimentKey: null })}>
            취소
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={attachmentDialog.open} 
        onClose={() => setAttachmentDialog({ open: false, urls: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>첨부파일</DialogTitle>
        <DialogContent>
          {attachmentDialog.urls.length === 0 ? (
            <DialogContentText>첨부파일이 없습니다.</DialogContentText>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {attachmentDialog.urls.map((url, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <AttachmentIcon fontSize="small" color="action" />
                  <Box
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textDecoration: 'none',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {url}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachmentDialog({ open: false, urls: [] })}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={responseTextDialog.open} 
        onClose={() => setResponseTextDialog({ open: false, text: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>응답 텍스트 상세보기</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mt: 1,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.default',
              maxHeight: '60vh',
              overflowY: 'auto',
              '& p': {
                margin: '0 0 1em 0',
                '&:last-child': {
                  marginBottom: 0,
                },
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: '1em',
                marginBottom: '0.5em',
                '&:first-child': {
                  marginTop: 0,
                },
              },
              '& ul, & ol': {
                marginLeft: '1.5em',
                marginBottom: '1em',
              },
              '& code': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                padding: '0.2em 0.4em',
                borderRadius: '3px',
                fontFamily: 'monospace',
              },
              '& pre': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                padding: '1em',
                borderRadius: '4px',
                overflow: 'auto',
                '& code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                },
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'divider',
                paddingLeft: '1em',
                marginLeft: 0,
                fontStyle: 'italic',
                color: 'text.secondary',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }}
          >
            {responseTextDialog.text ? (
              <ReactMarkdown>{responseTextDialog.text}</ReactMarkdown>
            ) : (
              '-'
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseTextDialog({ open: false, text: '' })}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogPage;
